import { CollaborationInput } from "./collaboration-input";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useEffect, useId, useState } from "react";
import * as SwitchPrimitive from "@radix-ui/react-switch";

import { useTrackEvent } from "libs/analytics";
import { COLLABORATION_VISIBILITY_COPY } from "./constants";
import { Tooltip } from "@dialogueconsulting/sked-ui";

import { COLLABORATION_VISIBILITY, CollaborationVisibility } from "./constants";
import { ColCollaborator, ColComment } from "../types";
import { User } from "@/features/user";

export const internalThemeClasses = cn(
  "[--collab-accent:var(--color-blue-500)]",
  "[--collab-accent-hover:var(--color-blue-50)]",
  "[--collab-surface:var(--color-blue-25)]",
  "[--collab-border:var(--color-blue-100)]",
  "[--collab-border-focus:var(--color-blue-300)]",
  "[--collab-note-bg:var(--color-blue-50)]",
  "[--collab-note-text:var(--color-blue-500)]"
);

export const externalThemeClasses = cn(
  "[--collab-accent:var(--color-warning-500)]",
  "[--collab-accent-hover:var(--color-warning-50)]",
  "[--collab-surface:var(--color-warning-25)]",
  "[--collab-border:var(--color-warning-100)]",
  "[--collab-border-focus:var(--color-warning-300)]",
  "[--collab-note-bg:var(--color-warning-50)]",
  "[--collab-note-text:var(--color-warning-700)]"
);

export function VisibilityToggleControl({
  toggleId,
  isExternal,
  isEditing,
  isToggleDisabled,
  displayName,
  onCheckedChange,
}: {
  toggleId: string;
  isExternal: boolean;
  isEditing: boolean;
  isToggleDisabled: boolean;
  displayName: string;
  onCheckedChange: (checked: boolean) => void;
}) {
  const visibilityActionLabel = isExternal
    ? isEditing
      ? "Update external note as "
      : "Send an external note as "
    : isEditing
      ? "Update internal comment as "
      : "Send an internal comment as ";

  return (
    <label
      htmlFor={toggleId}
      className={cn(
        "mb-0 flex min-w-0 items-center gap-[8px] rounded-[4px]",
        "bg-[var(--collab-note-bg)] px-[6px] py-[6px] text-[10px] leading-[14px] text-[#333] font-[400]",
        isToggleDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
      )}
    >
      <SwitchPrimitive.Root
        id={toggleId}
        checked={isExternal}
        onCheckedChange={onCheckedChange}
        disabled={isToggleDisabled}
        className={cn(
          "relative inline-flex h-[20px] w-[36px] shrink-0 items-center rounded-full border border-transparent bg-[var(--color-neutral-200)] p-[2px]",
          "transition-[background-color,border-color] duration-200 ease-linear",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--collab-accent,rgba(79,49,255,0.45))] focus-visible:ring-offset-white",
          "data-[state=checked]:border-transparent data-[state=checked]:bg-[var(--collab-accent)]",
          "data-[state=checked]:!bg-[var(--collab-accent)]",
          "data-[state=unchecked]:border data-[state=unchecked]:border-[var(--color-neutral-200)]",
          "data-[state=unchecked]:!bg-[var(--collab-surface)]",
          "data-[state=unchecked]:!border-[var(--collab-border)]",
          "disabled:cursor-not-allowed disabled:opacity-60"
        )}
      >
        <SwitchPrimitive.Thumb
          className={cn(
            "pointer-events-none block h-[16px] w-[16px] rounded-full bg-white shadow-sm",
            "transition-transform duration-200 ease-linear",
            "data-[state=checked]:translate-x-[16px] data-[state=unchecked]:translate-x-0"
          )}
        />
      </SwitchPrimitive.Root>
      <span className="truncate">
        {visibilityActionLabel}
        <span className="font-semibold text-[#1f1f1f]">{displayName}</span>
      </span>
    </label>
  );
}

export function CollaborationInputControlsV2({
  collaborators,
  onMessageSend,
  editingComment,
  disabled,
  currentUser,
  onCancelEdit,
  isError,
  onClearError,
}: {
  collaborators: ColCollaborator[];
  onMessageSend: (
    message: string,
    visibility: CollaborationVisibility
  ) => Promise<boolean>;
  editingComment?: ColComment | null;
  disabled?: boolean;
  currentUser: User;
  onCancelEdit?: () => void;
  isError: boolean;
  onClearError: () => void;
}) {
  const trackEvent = useTrackEvent();

  const [inputValue, setInputValue] = useState("");
  const [visibility, setVisibility] = useState<CollaborationVisibility>(
    COLLABORATION_VISIBILITY.INTERNAL
  );

  const visibilityCopy = COLLABORATION_VISIBILITY_COPY[visibility];
  const toggleId = useId();

  const displayName =
    [currentUser?.firstName, currentUser?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim()
      .replaceAll(/\s{2,}/g, " ") ||
    currentUser?.userName ||
    currentUser?.email ||
    "you";

  const handleValueChange = (value: string) => {
    setInputValue(value);
  };

  useEffect(() => {
    if (editingComment) {
      const { body, visibility: commentVisibility } = editingComment;
      const message = body;

      handleValueChange(message);
      setVisibility(
        commentVisibility === COLLABORATION_VISIBILITY.EXTERNAL
          ? COLLABORATION_VISIBILITY.EXTERNAL
          : COLLABORATION_VISIBILITY.INTERNAL
      );
    } else {
      handleValueChange("");
      setVisibility(COLLABORATION_VISIBILITY.INTERNAL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingComment]);

  // on plane icon click or enter press
  const sendMessage = async () => {
    const message = inputValue || "";

    if (!message.trim()) {
      return;
    }

    const rs = await onMessageSend(message, visibility);
    if (rs) {
      handleValueChange("");
    }

    trackEvent({
      eventName: "collab-send-comment",
      metadata: {
        type: visibility,
      },
      useServices: ["AMP"],
    });

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).hj("event", visibilityCopy.analyticsEvent);
      console.log("Hotjar trigger", visibilityCopy.analyticsEvent);
    } catch (error) {
      console.error("Hotjar fire event error:", error);
    }
  };

  const handleVisibilityToggle = (checked: boolean) => {
    if (
      currentUser?.role === "collaborator" &&
      currentUser?.preferences?.canCreateExternalComments === false
    ) {
      return;
    }
    setVisibility(
      checked
        ? COLLABORATION_VISIBILITY.EXTERNAL
        : COLLABORATION_VISIBILITY.INTERNAL
    );
  };

  const isEditing = Boolean(editingComment);

  const isSendDisabled = inputValue.trim().length === 0 || disabled;
  const isCollaboratorWithoutExternalAccess =
    currentUser?.role === "collaborator" &&
    currentUser?.preferences?.canCreateExternalComments === false;
  const isSwitchDisabled = disabled || isEditing;
  const isToggleDisabled =
    isSwitchDisabled || isCollaboratorWithoutExternalAccess;
  const adminEmail = currentUser?.adminEmail ?? currentUser?.email ?? "";
  const restrictedToggleTooltip = adminEmail
    ? `To turn on External note, contact your admin ${adminEmail}`
    : "To turn on External note, contact your admin.";

  const isExternal = visibility === COLLABORATION_VISIBILITY.EXTERNAL;

  const visibilityJsx = (
    <VisibilityToggleControl
      toggleId={toggleId}
      isExternal={isExternal}
      isEditing={isEditing}
      isToggleDisabled={isToggleDisabled}
      displayName={displayName}
      onCheckedChange={handleVisibilityToggle}
    />
  );

  return (
    <div
      data-visibility={visibility}
      className={cn(
        "mt-[8px]",
        "relative flex flex-col gap-[12px]",
        "bg-[var(--color-shades-white)]",
        "[font-family:var(--font-inter)]",
        isExternal ? externalThemeClasses : internalThemeClasses
      )}
    >
      <div className="relative flex flex-col gap-[8px]">
        {/* <SendError /> */}
        {isError && <SendError onClose={onClearError} />}
        <CollaborationInput
          disabled={disabled}
          isExternal={isExternal}
          isError={isError}
          collaborators={collaborators}
          onChange={(value) => {
            if (isError) {
              onClearError();
            }
            handleValueChange(value);
          }}
          value={inputValue}
          onSubmit={() => {
            sendMessage();
          }}
        />
      </div>

      <div className="relative flex w-full items-center gap-[12px]">
        {isCollaboratorWithoutExternalAccess ? (
          <Tooltip text={restrictedToggleTooltip} side="bottom">
            {visibilityJsx}
          </Tooltip>
        ) : (
          visibilityJsx
        )}

        {isEditing && (
          <Button
            size="xs"
            type="button"
            variant="blue-secondary"
            className="ml-auto"
            onClick={() => onCancelEdit?.()}
          >
            Cancel
          </Button>
        )}

        <Button
          size="xs"
          type="button"
          onClick={sendMessage}
          disabled={isSendDisabled}
          variant={isExternal ? "orange-primary" : "blue-primary"}
          className={cn(isEditing ? "" : "ml-auto")}
        >
          {isEditing ? "Save" : "Send"}
        </Button>
      </div>
    </div>
  );
}

function SendError({ onClose }: { onClose: () => void }) {
  return (
    <div
      className={`px-[12px] py-[10px] bg-red-50 text-red-500 flex gap-[8px] text-[12px] leading-[16px] 
    items-center rounded-[8px] font-semibold self-end`}
    >
      <i className="fa-regular fa-circle-xmark text-[15px]" />
      <span>Your message wasn’t sent. Please try again later.</span>
      <i
        className="fa-regular fa-close ml-[8px] cursor-pointer text-[15px]"
        onClick={onClose}
      />
    </div>
  );
}
